import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutGrid,
  CreditCard,
  HelpCircle,
  Settings,
  LogOut,
  User,
  Menu,
  X
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuItems = [
    { path: '/dashboard', icon: LayoutGrid, label: 'Dashboard' },
    { path: '/billing', icon: CreditCard, label: 'Faturação', badge: !user?.hasPaymentMethod },
    { path: '/support', icon: HelpCircle, label: 'Ajuda' },
    { path: '/settings', icon: Settings, label: 'Definições' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background flex">
      {/* Compact Sidebar - Desktop */}
      <aside className="hidden md:flex md:flex-col w-20 bg-white border-r border-gray-200 fixed h-full">
        {/* Logo */}
        <div className="h-20 flex items-center justify-center border-b border-gray-100">
          <img src="/Layers.svg" alt="Logo" className="w-7 h-7" />
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 py-6 flex flex-col items-center gap-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                className="relative group"
              >
                <div className={`
                  w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200
                  ${active
                    ? 'bg-[#E8F4FF] text-primary'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}>
                  <Icon className="w-5 h-5" />
                  {item.badge && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* User Menu at Bottom */}
        <div className="pb-6 flex flex-col items-center gap-2 border-t border-gray-100 pt-4">
          <button
            onClick={handleLogout}
            className="relative group w-12 h-12 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-50 flex items-center justify-between px-4">
        <div className="w-8 h-8 from-primary to-primary rounded-lg flex items-center justify-center">
          <img src="/Layers.svg" alt="Logo" />
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-gray-600 hover:text-gray-900"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-white z-40 pt-16">
          <nav className="flex flex-col p-4 gap-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative
                    ${active
                      ? 'bg-[#E8F4FF] text-primary font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {item.badge && (
                    <span className="w-2 h-2 bg-red-500 rounded-full ml-auto"></span>
                  )}
                </Link>
              );
            })}

            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors mt-4"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:ml-20">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 h-16 md:h-20 flex items-center justify-between px-6 md:px-8 mt-16 md:mt-0">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              {location.pathname === '/dashboard' && 'Dashboard'}
              {location.pathname === '/billing' && 'Billing'}
              {location.pathname === '/support' && 'Suporte'}
              {location.pathname === '/settings' && 'Definições'}
            </h1>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-gray-600" />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
